/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHotkeys } from 'react-hotkeys-hook';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDSnackbar from 'components/MDSnackbar';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';

// Authentication layout components
import * as Func from 'services/Function';
import { FetchData } from 'services/Request';
import PosPrint from './PosPrint';

import './PointOfSale.css';

const initCart = {
  id: '',
  customer_id: '',
  customer_name: '',
  nettotal: '',
  discount_all: '',
};

function PosTransfer({
  cboBank,
  reload,
  isDisabled,
  setBlockScan,
  sizeButton,
  buttonLoading,
  setButtonLoading,
}) {
  const mod = 'pointofsale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [cart, setCart] = useState(initCart);
  const [cartList, setCartList] = useState([]);
  const [campaign, setCampaign] = useState([]);
  const printRef = useRef();
  const btnRef = useRef(null);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const close = () => {
    setModalOpen(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const checkout = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/checkout`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          if (result.pharmacy === 'N') {
            showNotify('กรุณาเพิ่มข้อมูลลูกค้า', 'error');
          } else {
            setCart(result.cart);
            setCartList(result.cart_list);
            setCampaign(result.campaign);
            setModalOpen(true);
          }
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const payment = (account_id) => {
    if (loading) return;

    setLoading(true);

    if (
      cart.pay_split === 'N' ||
      (cart.pay_split === 'Y' &&
        Number(cart.paid) + Number(cart.pay_transfer) >= Number(cart.nettotal))
    ) {
      localStorage.setItem(
        'pos_status',
        JSON.stringify({
          step: 'payment',
          status: 'fetching',
          paymentType: 'TRANSFER',
          cartId: cart.id,
        })
      );
    }

    FetchData('OPTIONS', `/${mod}/payment`, app.token, {
      payment_type: 'TRANSFER',
      account_id: account_id,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setModalOpen(false);
          reload();
          showNotify(t(`alert.addcomplete`), 'success');

          const status = JSON.parse(localStorage.getItem('pos_status'));
          if (
            status?.step === 'payment' &&
            status?.status === 'fetching' &&
            status?.paymentType === 'TRANSFER'
          ) {
            delete status.cartId;
            localStorage.setItem(
              'pos_status',
              JSON.stringify({ ...status, status: 'success', so_id: result.id })
            );
          } else {
            showNotify('เกิดข้อผิดพลาดในการบันทึกสถานะ Local', 'error');
          }

          printRef.current.print(result.id);
        } else if (result.status === 'paid') {
          setCart(result.cart);
          close();
          reload();
          showNotify(t(`alert.addcomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={0}>
          <Grid item xs={12} sx={{ borderBottom: '1px solid #cccccc;' }}>
            <TableContainer
              sx={{
                height: 335,
                boxShadow: 0,
              }}
            >
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>No.</TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell align='center'>โปรโมชั่น</TableCell>
                    <TableCell align='right'>จำนวน</TableCell>
                    <TableCell align='left'>หน่วย</TableCell>
                    <TableCell align='right'>ราคา</TableCell>
                    <TableCell align='right'>รวม</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartList.map((row, i) => (
                    <TableRow className='row-order' key={row.id}>
                      <TableCell align='center'>{i + 1}</TableCell>
                      <TableCell>{row.product_name}</TableCell>
                      <TableCell align='center'>{row.campaign_code}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.quantity)}</TableCell>
                      <TableCell align='left'>{row.unit_name}</TableCell>
                      <TableCell align='right'>
                        {row.free === 'Y' ? '' : Func.numberDisplay(row.price)}
                      </TableCell>
                      <TableCell align='right'>
                        {row.free === 'Y' ? <code>แถม</code> : Func.numberDisplay(row.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} sm={12} md={7}>
            <TableContainer sx={{ height: 200, boxShadow: 0 }}>
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>โปรโมชั่น</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaign.map((row, i) => (
                    <TableRow className='row-order' key={row.id}>
                      <TableCell width={80}>{row.code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} sm={12} md={5} sx={{ borderLeft: '1px solid #cccccc;' }}>
            <TableContainer sx={{ boxShadow: 0 }}>
              <Table aria-label='summary table'>
                <TableBody>
                  {cart.problem_type === 'return' ? (
                    <TableRow>
                      <TableCell align='right'>
                        <MDTypography variant='h5'>มูลค่าการคืน :</MDTypography>
                      </TableCell>
                      <TableCell align='right'>
                        <MDTypography variant='h5'>
                          {Func.numberDisplay(cart.return_total, 2)}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>รวม :</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>{Func.numberDisplay(cart.total, 2)}</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>ส่วนลด :</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(cart.discount_all, 2)}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>รวมทั้งสิ้น :</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h4' color='info'>
                        {Func.numberDisplay(cart.nettotal, 2)}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                  {cart.pay_split === 'Y' ? (
                    <TableRow>
                      <TableCell align='right'>
                        <MDTypography variant='h5'>แยกชำระ :</MDTypography>
                      </TableCell>
                      <TableCell align='right'>
                        <MDTypography variant='h4' color='error'>
                          {Func.numberDisplay(cart.pay_transfer, 2)}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderBank = () => {
    return (
      <MDBox sx={{ height: 535, overflowY: 'scroll', overflowX: 'hidden' }}>
        {cboBank.map((item) => (
          <button
            type='button'
            key={item.id}
            className='btnbank'
            onClick={() => {
              payment(item.id);
            }}
          >
            <div className='myaccount' style={{ backgroundColor: item.bank_color }}>
              <table width='100%'>
                <tbody>
                  <tr>
                    <td width='90'>
                      <img src={item.bank_pic} width='70' alt={item.bank_name} />
                    </td>
                    <td>
                      <div>{item.bank_name}</div>
                      <div className='text-account'>{item.acc_no}</div>
                      <div className='wrap'>{item.acc_name}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </button>
        ))}
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          <i className='fad fa-mobile-alt color-danger' /> &nbsp; ชำระเงินโอน
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6} lg={7} xl={8}>
              <MDBox className='card'>{renderList()}</MDBox>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={5} xl={4}>
              {renderBank()}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    );
  };

  useHotkeys('d', () => checkout());

  return (
    <MDBox>
      <Tooltip title={isDisabled ? '' : 'KEY : D'}>
        <MDButton
          variant='contained'
          color='warning'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={checkout}
          disabled={isDisabled || loading || buttonLoading}
          ref={btnRef}
        >
          {loading ? (
            <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
          ) : (
            <i className='fad fa-mobile-alt' />
          )}
          &nbsp; เงินโอน
        </MDButton>
      </Tooltip>
      <PosPrint ref={printRef} paymentType='TRANSFER' />
      {renderOpen()}
      {notify}
    </MDBox>
  );
}

PosTransfer.propTypes = {
  cboBank: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      bank_color: PropTypes.string.isRequired,
      bank_pic: PropTypes.string.isRequired,
      bank_name: PropTypes.string.isRequired,
      acc_no: PropTypes.string.isRequired,
      acc_name: PropTypes.string.isRequired,
    })
  ).isRequired,
  reload: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  sizeButton: PropTypes.number.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};

export default PosTransfer;
